import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useBook } from '../../contexts/BookContext';
import { useComments } from '../../contexts/CommentContext';
import { Comment } from '../../types/comment';
import { BookSection } from '../../types/book';
import { SearchResult } from '../../types/search';
import { bookService } from '../../services/book';
import { searchService } from '../../services/search';
import CommentableMarkdownContent from '../comments/CommentableMarkdownContent';
import CommentsPanel from '../comments/CommentsPanel';
import TableOfContents from '../common/TableOfContents/TableOfContents';
import NavigationControls from '../common/NavigationControls/NavigationControls';
import SearchPanel from '../common/SearchPanel/SearchPanel';
import AdminCommentManager from '../dashboard/AdminCommentManager';
import MobileNavigation from '../common/MobileNavigation/MobileNavigation';
import { useKeyboardNavigation } from '../../hooks/useKeyboardNavigation';
import { getNavigationContext, scrollToTopWithDelay } from '../../utils/navigationUtils';
import bookIndexComplete from '../../content/book-index-complete.json';

interface NavigationItem {
  id: string;
  title: string;
  level: number;
  children?: NavigationItem[];
  filename?: string;
}

interface BookIndex {
  title: string;
  description: string;
  metadata: {
    author: string;
    version: string;
    lastUpdated: string;
  };
  navigation: NavigationItem[];
  totalSections: number;
}

const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const { isLoading, setCurrentSection } = useBook();
  const { comments, loadComments, createComment, updateComment, deleteComment } = useComments();
  const [selectedComment, setSelectedComment] = useState<Comment | null>(null);
  const [currentSectionId, setCurrentSectionId] = useState<string>('1-1');
  const [currentSectionContent, setCurrentSectionContent] = useState<BookSection | null>(null);
  const [loadingSection, setLoadingSection] = useState<boolean>(false);
  const [bookIndex, setBookIndex] = useState<BookIndex | null>(null);
  const [loadingBookIndex, setLoadingBookIndex] = useState<boolean>(true);
  const [searchIndexBuilt, setSearchIndexBuilt] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<'reading' | 'admin'>('reading');
  const [isAdmin, setIsAdmin] = useState<boolean>(false);

  // Check if user is admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (user) {
        try {
          // Check if user has admin privileges
          const { data: profile } = await import('../../lib/supabase').then(({ supabase }) =>
            supabase
              .from('user_profiles')
              .select('is_admin')
              .eq('id', user.id)
              .single()
          );
          setIsAdmin(profile?.is_admin || false);
        } catch (error) {
          console.error('Error checking admin status:', error);
          setIsAdmin(false);
        }
      }
    };

    checkAdminStatus();
  }, [user]);

  // Load book index on component mount
  useEffect(() => {
    const loadBookIndex = () => {
      try {
        // Use the imported JSON data instead of fetching
        setBookIndex(bookIndexComplete as BookIndex);
      } catch (error) {
        console.error('Failed to load book index:', error);
      } finally {
        setLoadingBookIndex(false);
      }
    };

    loadBookIndex();
  }, []);

  // Batch loading operations to reduce re-renders
  useEffect(() => {
    if (currentSectionId) {
      // Load both comments and content together to reduce re-renders
      const loadSectionData = async () => {
        try {
          // Start both operations in parallel
          const [commentsPromise, contentPromise] = [
            loadComments(currentSectionId),
            loadSectionContent(currentSectionId)
          ];
          
          // Wait for both to complete
          await Promise.all([commentsPromise, contentPromise]);
        } catch (error) {
          console.error('Error loading section data:', error);
        }
      };
      
      loadSectionData();
    }
  }, [currentSectionId, loadComments]);

  // Build search index when book is loaded
  useEffect(() => {
    const buildSearchIndex = async () => {
      if (bookIndex && !searchIndexBuilt) {
        try {
          // Load the full book with content for search indexing
          const book = await bookService.getBookWithContent();
          searchService.buildIndex(book);
          setSearchIndexBuilt(true);
          console.log('Search index built successfully');
        } catch (error) {
          console.error('Failed to build search index:', error);
        }
      }
    };

    buildSearchIndex();
  }, [bookIndex, searchIndexBuilt]);

  const loadSectionContent = async (sectionId: string) => {
    setLoadingSection(true);
    try {
      const section = await bookService.getSection(sectionId);
      
      // Preload images before setting content to prevent visual bounce
      await preloadImagesInContent(section.content);
      
      setCurrentSectionContent(section);
      setCurrentSection(sectionId); // Update the book context
    } catch (error) {
      console.error('Failed to load section content:', error);
      setCurrentSectionContent(null);
    } finally {
      setLoadingSection(false);
    }
  };

  // Helper function to preload images in markdown content
  const preloadImagesInContent = async (content: string): Promise<void> => {
    return new Promise((resolve) => {
      // Extract image URLs from markdown content
      const imageRegex = /!\[([^\]]*)\]\(([^)]+)\)/g;
      const imageUrls: string[] = [];
      let match;
      
      while ((match = imageRegex.exec(content)) !== null) {
        imageUrls.push(match[2]);
      }
      
      if (imageUrls.length === 0) {
        resolve();
        return;
      }
      
      let loadedCount = 0;
      const totalImages = imageUrls.length;
      
      // Set a timeout to prevent hanging on broken images
      const timeout = setTimeout(() => {
        console.warn('Image preloading timeout - proceeding with render');
        resolve();
      }, 2000); // 2 second timeout
      
      const checkComplete = () => {
        loadedCount++;
        if (loadedCount >= totalImages) {
          clearTimeout(timeout);
          resolve();
        }
      };
      
      // Preload each image
      imageUrls.forEach(url => {
        const img = new Image();
        img.onload = checkComplete;
        img.onerror = checkComplete; // Count errors as "loaded" to prevent hanging
        img.src = url;
      });
    });
  };

  const handleSectionClick = async (sectionId: string) => {
    setCurrentSectionId(sectionId);
    setSelectedComment(null); // Clear selected comment when switching sections
    // Enhanced scroll to top with content loading coordination
    await scrollToTopWithDelay(150);
  };

  // Navigation functions
  const navigationContext = bookIndex ? getNavigationContext(currentSectionId, bookIndex.navigation) : null;

  const handlePrevious = () => {
    if (navigationContext?.previous) {
      handleSectionClick(navigationContext.previous.id);
    }
  };

  const handleNext = () => {
    if (navigationContext?.next) {
      handleSectionClick(navigationContext.next.id);
    }
  };

  // Search handlers
  const handleSearch = (query: string): SearchResult[] => {
    if (!searchIndexBuilt) {
      console.warn('Search index not ready yet');
      return [];
    }
    return searchService.search(query);
  };

  const handleNavigateToSearchResult = (result: SearchResult) => {
    handleSectionClick(result.sectionId);
  };

  // Keyboard navigation
  useKeyboardNavigation({
    onPrevious: handlePrevious,
    onNext: handleNext,
    hasPrevious: !!navigationContext?.previous,
    hasNext: !!navigationContext?.next,
    enabled: true
  });

  if (isLoading || loadingBookIndex) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div className="spinner" style={{ margin: '2rem auto' }}></div>
        <p>Loading your book content...</p>
      </div>
    );
  }

  if (!bookIndex) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div className="alert alert-error">
          <strong>Error loading book content.</strong>
          <br />
          Please refresh the page to try again.
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '80vh', padding: '2rem' }}>
      <div style={{ maxWidth: '95vw', margin: '0 auto' }}>

        {/* Mobile Unified Navigation - TOC + Read/Admin Icons in one row */}
        <div className="mobile-unified-nav" style={{ 
          display: 'none',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem',
          gap: '0.5rem'
        }}>
          {/* TOC Hamburger - Left side */}
          <div style={{ display: 'block' }}>
            <MobileNavigation title="Table of Contents">
              <TableOfContents
                navigation={bookIndex.navigation}
                currentSectionId={currentSectionId}
                onSectionClick={handleSectionClick}
              />
            </MobileNavigation>
          </div>
          
          {/* Read/Admin Icons - Right side */}
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={() => setActiveTab('reading')}
              style={{
                padding: '0.75rem',
                backgroundColor: activeTab === 'reading' ? '#2563eb' : '#f3f4f6',
                color: activeTab === 'reading' ? 'white' : '#374151',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1.25rem',
                minWidth: '44px',
                minHeight: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Reading Mode"
            >
              📖
            </button>
            {isAdmin && (
              <button
                onClick={() => setActiveTab('admin')}
                style={{
                  padding: '0.75rem',
                  backgroundColor: activeTab === 'admin' ? '#2563eb' : '#f3f4f6',
                  color: activeTab === 'admin' ? 'white' : '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '1.25rem',
                  minWidth: '44px',
                  minHeight: '44px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Admin Mode"
              >
                ⚙️
              </button>
            )}
          </div>
        </div>

        {/* Conditional Layout Based on Active Tab */}
        {activeTab === 'admin' ? (
          /* Admin Layout - Full Width */
          <AdminCommentManager />
        ) : (
          /* Reading Layout - Responsive Grid */
          <div className="dashboard-layout">
            {/* Mobile Navigation for Table of Contents */}
            <div className="dashboard-sidebar">
              {/* Mobile Navigation Toggle (hidden on mobile, moved to unified nav) */}
              <div style={{ display: 'none' }} className="mobile-nav-container">
                <MobileNavigation title="Table of Contents">
                  <TableOfContents
                    navigation={bookIndex.navigation}
                    currentSectionId={currentSectionId}
                    onSectionClick={handleSectionClick}
                  />
                </MobileNavigation>
              </div>
              
              {/* Desktop Table of Contents (hidden on mobile) */}
              <div className="card desktop-toc" style={{ 
                height: 'fit-content', 
                position: 'sticky', 
                top: '2rem',
                display: 'none'
              }}>
                <TableOfContents
                  navigation={bookIndex.navigation}
                  currentSectionId={currentSectionId}
                  onSectionClick={handleSectionClick}
                />
              </div>
            </div>

            {/* Main Content Area */}
            <div className="card dashboard-content">
              {/* Book Title Header - Hidden on Mobile */}
              <div className="card-header book-title-header">
                <h2 className="card-title">{bookIndex.title}</h2>
                {/* <p style={{ color: '#666', margin: 0 }}>
                  By {bookIndex.metadata.author} • Version {bookIndex.metadata.version}
                </p> */}
              </div>

              <div style={{ lineHeight: '1.6', fontSize: '1rem' }}>
                {/* Welcome Instructions - Hidden on Mobile */}
                <div className="alert alert-info welcome-instructions" style={{ marginBottom: '2rem' }}>
                  {/* <strong>Welcome to your personalized reading experience!</strong> */}
                  {/* <br /> */}
                  Navigate with arrow keys (← →), add comments by clicking on content.
                </div>

                {(() => {
                  if (loadingSection) {
                    return (
                      <div style={{ marginBottom: '3rem', textAlign: 'center', padding: '2rem' }}>
                        <div className="spinner" style={{ margin: '1rem auto' }}></div>
                        <p>Loading section content...</p>
                      </div>
                    );
                  }

                  const currentSection = navigationContext?.current;
                  if (!currentSection) {
                    return (
                      <div className="alert alert-warning">
                        <strong>Section not found.</strong>
                        <br />
                        Please select a section from the table of contents.
                      </div>
                    );
                  }

                  // Use the dynamically loaded content if available, otherwise fallback to placeholder
                  const contentToDisplay = currentSectionContent?.content || 
                    'Medical device cybersecurity is a critical aspect of healthcare technology that ensures the safety, effectiveness, and security of medical devices throughout their lifecycle. This comprehensive guide covers the essential principles, regulatory requirements, and best practices for implementing robust cybersecurity measures in medical device development and deployment. Understanding these fundamentals is crucial for manufacturers, healthcare providers, and regulatory bodies working to protect patient safety and data integrity in an increasingly connected healthcare environment.';

                  return (
                    <div style={{ marginBottom: '3rem' }}>
                      <CommentableMarkdownContent
                        content={contentToDisplay}
                        sectionId={currentSection.id}
                        comments={comments}
                        onAddComment={createComment}
                        onCommentClick={setSelectedComment}
                        selectedCommentId={selectedComment?.id || null}
                      />

                      {/* Navigation Controls */}
                      <NavigationControls
                        onPrevious={handlePrevious}
                        onNext={handleNext}
                        hasPrevious={!!navigationContext?.previous}
                        hasNext={!!navigationContext?.next}
                        currentSectionTitle={currentSection.title}
                        previousSectionTitle={navigationContext?.previous?.title}
                        nextSectionTitle={navigationContext?.next?.title}
                      />
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Search and Comments Panel */}
            <div className="dashboard-panel" style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '1rem', 
              position: 'sticky', 
              top: '2rem', 
              height: 'fit-content' 
            }}>
              {/* Tab Navigation - Desktop (in right panel) */}
              {isAdmin && (
                <div className="card" style={{ padding: '1rem' }}>
                  <div style={{ 
                    display: 'flex', 
                    gap: '0.5rem', 
                    justifyContent: 'center'
                  }}>
                    <button
                      onClick={() => setActiveTab('reading')}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: activeTab === 'reading' ? '#2563eb' : '#f3f4f6',
                        color: activeTab === 'reading' ? 'white' : '#374151',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        fontWeight: activeTab === 'reading' ? 600 : 400,
                        flex: 1
                      }}
                    >
                      📖 Reading
                    </button>
                    <button
                      onClick={() => setActiveTab('admin')}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: activeTab !== 'reading' ? '#2563eb' : '#f3f4f6',
                        color: activeTab !== 'reading' ? 'white' : '#374151',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        fontWeight: activeTab !== 'reading' ? 600 : 400,
                        flex: 1
                      }}
                    >
                      ⚙️ Admin
                    </button>
                  </div>
                </div>
              )}

              {/* Search Panel */}
              <SearchPanel
                onSearch={handleSearch}
                onNavigateToResult={handleNavigateToSearchResult}
                currentSectionId={currentSectionId}
              />
              
              {/* Comments Panel */}
              <CommentsPanel
                comments={comments}
                selectedComment={selectedComment}
                onCommentSelect={setSelectedComment}
                onDeleteComment={deleteComment}
                onUpdateComment={(commentId, content) => updateComment({ id: commentId, content })}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
